// @code-guard: skip-file reason="Large legacy chart container will be split and tokenized in follow-up refactor"
import { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, MutableRefObject, ReactNode, RefObject } from 'react';
import { Spin } from 'antd';

import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import VerticalRangeSlider from 'components/molecules/common/VerticalRangeSlider';
import { colorConstants } from 'config/colorConstants';
import { FilterContextData } from 'contexts/FilterContext';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { getAxisLabel } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { buildPackArchitectureGlobalFiltersSummary } from 'components/pages/PackArchitecture/packArchitectureGlobalFilters.helpers';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { formatNumWithKMBAlt } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_PACK_ARCH_BUBBLE_HOVER,
    PPA_PACK_ARCH_BUBBLE_OR_CELL_SELECTED,
    PPA_PACK_ARCH_CHART_LAZY_LOAD_TRIGGERED,
    PPA_PACK_ARCH_CHART_RENDERED,
    PPA_PACK_ARCH_EMPTY_STATE_VIEWED,
    PPA_PACK_ARCH_HEATMAP_FILTERED_VIEW_USED,
    PPA_PACK_ARCH_LEGEND_EXPANDED,
    PPA_PACK_ARCH_LEGEND_OTHER_EXPANDED,
    PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED,
    PPA_PACK_ARCH_ZOOM_CHANGED,
} from 'utils/mixpanelEventNames';
import BubbleScatterChart, {
    type ActiveSegmentBreakpoints,
    type YAxisBreakpointLabelPosition,
    type YAxisTickLabelPosition,
} from './BubbleScatterChart';
import InboxOutlined from 'assets/svg/InboxOutlined';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import type { AxisGroup, KpiItem, PricePackBubbleRow, SegmentItem } from 'hooks/ppa/useFetchPricePackArchitecture';
import type { PpaChartDimensionMember } from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import {
    getPackArchitectureEntityKey,
    formatPackArchitectureKpiValue,
    EMPTY_CELL_VALUE,
    computePpaBubbleSizing,
    PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO,
    PPA_CHART_TOP_APPEND_PADDING_PX,
    PPA_CHART_X_AXIS_CHROME_HEIGHT_PX,
} from './packArchitectureTable.helpers';
import { getYZoomPercentage, shouldTrackYZoomChange } from './packArchitectureZoomTracking.helpers';
import {
    PPA_PACK_ARCH_CHART_EMPTY,
    PPA_PACK_ARCH_CHART_ERROR,
    PPA_PACK_ARCH_CHART_LOADING,
    PPA_PACK_ARCH_LEGEND_EXPAND_BTN,
    ppaPackArchLegendChipTestId,
} from 'constants/ppaTestIds';

type SegmentLegend = {
    id: string;
    label: string;
    color: string;
    /** PA-03: true for the trailing aggregated "Other {Dimension}" legend
     * entry (from the backend's ranked `bubble_legend`, or inferred via
     * `isOtherLikeLabel` for the legacy chartRows-derived fallback). */
    isOther?: boolean;
    /** PA-03: the individual "bubble represents" values (identity only, no
     * KPI numbers) folded into the Other entry - lets the chip expand and
     * show what got grouped into it. Populated only when `isOther` is true. */
    otherMembers?: PpaChartDimensionMember[];
};

const LEGEND_COLOR_DOT_SIZE_PX = 8;
const DEFAULT_BUBBLE_COLOR = colorConstants.packArchitectureBubbleDefault;
/** Returns true for segment labels that represent a catch-all "Other" bucket
 * (e.g. "Other", "Others", or the PA-03 "Other {Dimension}" rollup label). */
const isOtherLikeLabel = (label: string): boolean => {
    const lower = label.trim().toLowerCase();
    return lower === 'other' || lower === 'others' || lower.startsWith('other ');
};

type LegendChipProps = {
    segment: SegmentLegend;
    isActive: boolean;
    isDimmed: boolean;
    onClick?: () => void;
    measureOnly?: boolean;
    displayOnly?: boolean;
    isBreakdownOpen?: boolean;
    onToggleBreakdown?: () => void;
    truncate?: boolean;
};

const LegendColorDot = ({ color }: { color: string }) => (
    <span
        aria-hidden
        className="shrink-0"
        style={{
            display: 'inline-block',
            width: LEGEND_COLOR_DOT_SIZE_PX,
            height: LEGEND_COLOR_DOT_SIZE_PX,
            minWidth: LEGEND_COLOR_DOT_SIZE_PX,
            minHeight: LEGEND_COLOR_DOT_SIZE_PX,
            borderRadius: '50%',
            backgroundColor: color || DEFAULT_BUBBLE_COLOR,
            boxSizing: 'border-box',
            flexShrink: 0,
        }}
    />
);

/** PA-03: expandable member list for the "Other {Dimension}" legend chip, so
 * a user can see which "bubble represents" values got folded into it. Shows
 * identity only (no KPI numbers), since these values were never
 * individually queried/shown. Rendered inline in the legend row (not a
 * popover) as siblings of the "Other {Dimension}" chip - each member gets
 * its own chip styled like every other legend entry, so the breakdown wraps
 * naturally with the rest of the (already flex-wrap) legend row instead of
 * living in its own horizontally-scrollable box. */
const OtherLegendBreakdownContent = ({
    otherMembers,
    color,
}: {
    otherMembers: PpaChartDimensionMember[];
    color: string;
}) => (
    <>
        {otherMembers.map((member) => (
            <span
                key={member.ex_id}
                className="m-0 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 text-xs leading-[18px] text-toolbarLabelGrey"
                title={member.description}
            >
                <LegendColorDot color={color} />
                {member.description}
            </span>
        ))}
    </>
);

const LegendChip: FC<LegendChipProps> = ({
    segment,
    isActive,
    isDimmed,
    onClick,
    measureOnly = false,
    displayOnly = false,
    isBreakdownOpen = false,
    onToggleBreakdown,
    truncate = false,
}) => {
    const interactive = !measureOnly && !displayOnly;
    const hasOtherBreakdown = Boolean(segment.isOther && segment.otherMembers?.length);
    const label = hasOtherBreakdown ? `${segment.label} (${segment.otherMembers?.length})` : segment.label;

    const handleChipClick = (event: { target: EventTarget }) => {
        if (!interactive) return;
        if ((event.target as HTMLElement).closest('[data-other-breakdown-toggle]')) {
            onToggleBreakdown?.();
            return;
        }
        onClick?.();
    };

    return (
        <button
            type="button"
            data-legend-chip={measureOnly ? '' : undefined}
            data-testid={measureOnly ? undefined : ppaPackArchLegendChipTestId(segment.id)}
            onClick={interactive ? handleChipClick : undefined}
            tabIndex={interactive ? 0 : -1}
            aria-disabled={displayOnly || undefined}
            className={`m-0 flex max-w-none shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 text-xs leading-[18px] shadow-none transition-all duration-150 ${
                interactive ? 'cursor-pointer' : 'cursor-default'
            } ${
                isActive
                    ? '  bg-transparent font-semibold text-toolbarLabelGrey'
                    : '  bg-transparent text-toolbarLabelGrey'
            } ${interactive && !isActive ? 'hover:bg-backgroundGreyAlt6 hover:font-medium' : ''} ${
                interactive ? 'active:border-purpleFontPressed' : ''
            }`}
            style={{ opacity: isDimmed ? 0.45 : 1 }}
            title={label}
        >
            {segment.isOther ? <span aria-hidden className="h-3 w-px shrink-0 bg-[#D9D9D9]" /> : null}
            <LegendColorDot color={segment.color} />
            <span className={truncate ? 'truncate' : ''}>{label}</span>
            {hasOtherBreakdown && !measureOnly ? (
                <span
                    data-other-breakdown-toggle
                    aria-hidden
                    className="-my-2 -mr-1.5 inline-flex shrink-0 items-center justify-center p-2"
                >
                    <HeaderSortDescChevron color={colorConstants.blackTitle} rotate={isBreakdownOpen ? 90 : -90} />
                </span>
            ) : null}
        </button>
    );
};

const countLegendChipsThatFit = (chipWidths: number[], rowWidth: number, moreButtonWidth: number): number => {
    if (chipWidths.length === 0) return 0;

    const totalWidth = chipWidths.reduce((sum, width, index) => sum + width + (index > 0 ? LEGEND_CHIP_GAP_PX : 0), 0);
    if (totalWidth <= rowWidth) return chipWidths.length;

    const available = rowWidth - moreButtonWidth - LEGEND_CHIP_GAP_PX;
    let used = 0;
    let count = 0;

    for (let index = 0; index < chipWidths.length; index++) {
        const chipWidth = chipWidths[index] + (index > 0 ? LEGEND_CHIP_GAP_PX : 0);
        if (count > 0 && used + chipWidth > available) break;
        used += chipWidth;
        count++;
    }

    return Math.max(1, count);
};

export type PackArchitectureChartWithControlsProps = {
    chartTitle: ReactNode;
    xAxisTitle: string;
    yAxisTitle: string;
    chartMinHeightPx?: number;
    horizontalScrollRef?: RefObject<HTMLDivElement | null>;
    /** True while table→chart scroll sync is applying — skip chart_scrolled Mixpanel. */
    isSyncingChartFromTableRef?: MutableRefObject<boolean>;
    loading?: boolean;
    showError?: boolean;
    errorMessage?: string;
    'data-testid'?: string;
};

const VISIBLE_LEGEND_LIMIT = 10;
const LEGEND_CHIP_GAP_PX = 12;
const LEGEND_MORE_LABEL_WIDTH_PX = 72;
const LEGEND_DROPDOWN_BUTTON_WIDTH_PX = 24;
const LEGEND_RIGHT_CONTROLS_GAP_PX = 8;
/** Expanded legend: multi-row wrap with vertical scroll (Figma). */
const LEGEND_EXPANDED_MAX_HEIGHT_PX = 128;
const DEFAULT_Y_MIN = 0;
const DEFAULT_Y_MAX = 100;
/** Fallback chart height before the ResizeObserver reports a real measurement. */
const DEFAULT_CHART_HEIGHT_PX = 416;
const CHART_AXIS_COLUMN_HEIGHT_OFFSET_PX = 26;
const CHART_AXIS_DIVIDER_HEIGHT_OFFSET_PX = 55;
const MIN_INITIAL_SCROLLABLE_EXTRA_WIDTH_PX = 0;
const PAGINATION_RIGHT_EDGE_THRESHOLD_PX = 16;
const PAGINATION_SCROLL_RETREAT_PX = 180;
const CHART_DECIMAL_PRECISION = 1;
const Y_ZOOM_SLIDER_STEP = Math.pow(10, -CHART_DECIMAL_PRECISION);
const Y_ZOOM_DRAG_DEBOUNCE_MS = 60;
const BUBBLE_HOVER_TRACK_MIN_MS = 3000;
const Y_AXIS_TICK_COUNT = 5;

type ChartBubbleRow = {
    key: string;
    columnKey: string;
    segmentKey: string;
    columnLabel: string;
    segmentLabel: string;
    xFieldValue: number;
    yFieldValue: number;
    bubbleSizeValue: number;
    bubbleColor: string;
    segmentBaseColor: string;
    valueText: string;
    avgText: string;
    volumeSalesText: string;
    growthText: string;
    growthPercentage: number | null;
    valueAverageLabel: string;
    growthDirection: 'up' | 'down' | 'neutral';
    tooltipKpis: Array<{
        id: string;
        label: string;
        value: string;
        growthText?: string;
        trendDirection?: string;
    }>;
    bubbleLabel: string;
    highlightLabel: string;
    isPinpointSelected: boolean;
};

type ActiveSelectionMode = 'segment' | 'column' | null;

type SegmentAggregate = {
    count: number;
    sumY: number;
    minY: number;
    maxY: number;
    sumGrowth: number;
    growthCount: number;
    segmentBaseColor: string;
};

const resolveActiveSelectionMode = (
    rows: Array<Pick<ChartBubbleRow, 'segmentKey' | 'columnKey'>>,
    selectionKey: string | null,
): ActiveSelectionMode => {
    if (!selectionKey) return null;

    let matchesColumn = false;
    for (const row of rows) {
        if (row.segmentKey === selectionKey) return 'segment';
        if (row.columnKey === selectionKey) matchesColumn = true;
    }

    return matchesColumn ? 'column' : null;
};

const buildSelectionAggregates = (rows: ChartBubbleRow[]) => {
    const segmentAggregates = new Map<string, SegmentAggregate>();
    const columnAggregates = new Map<string, SegmentAggregate>();

    const accumulate = (aggregates: Map<string, SegmentAggregate>, key: string, row: ChartBubbleRow) => {
        if (!key) return;
        const yValue = Number(row.yFieldValue);
        if (!Number.isFinite(yValue)) return;
        const existing = aggregates.get(key);
        if (!existing) {
            const growth = typeof row.growthPercentage === 'number' && !Number.isNaN(row.growthPercentage) ? row.growthPercentage : null;
            aggregates.set(key, {
                count: 1,
                sumY: yValue,
                minY: yValue,
                maxY: yValue,
                sumGrowth: growth ?? 0,
                growthCount: growth == null ? 0 : 1,
                segmentBaseColor: row.segmentBaseColor || row.bubbleColor || DEFAULT_BUBBLE_COLOR,
            });
            return;
        }

        existing.count += 1;
        existing.sumY += yValue;
        existing.minY = Math.min(existing.minY, yValue);
        existing.maxY = Math.max(existing.maxY, yValue);
        if (typeof row.growthPercentage === 'number' && !Number.isNaN(row.growthPercentage)) {
            existing.sumGrowth += row.growthPercentage;
            existing.growthCount += 1;
        }
    };

    rows.forEach((row) => {
        accumulate(segmentAggregates, row.segmentKey, row);
        accumulate(columnAggregates, row.columnKey, row);
    });

    return { segmentAggregates, columnAggregates };
};

const getKpi = (segment: SegmentItem, kpiId: string): KpiItem | undefined =>
    (segment.kpis ?? []).find((kpi) => String(kpi.kpi_id).toLowerCase() === kpiId.toLowerCase());
const NON_TOOLTIP_KPI_IDS = new Set(['color_code', 'radius']);

const toChartKpiDisplay = (kpi: KpiItem | undefined, kpiId: string, yAxisKpiId: string): string => {
    if (!kpi) return '-';
    const formatted = formatPackArchitectureKpiValue(kpi, kpiId, yAxisKpiId);
    return formatted === EMPTY_CELL_VALUE ? '-' : formatted;
};

const formatRangeLabel = (value: number) => value.toFixed(CHART_DECIMAL_PRECISION);

const getYAxisTickPositionKey = (value: number) => value.toFixed(CHART_DECIMAL_PRECISION);

const doesYAxisTickOverlapBreakpoint = (
    tick: number,
    tickTopPx: number | undefined,
    breakpointPositions: YAxisBreakpointLabelPosition[],
) => {
    if (breakpointPositions.length === 0) return false;
    const hasTickTop = typeof tickTopPx === 'number' && Number.isFinite(tickTopPx);

    return breakpointPositions.some(({ yValue, topPx }) => {
        const valuesMatch = Math.abs(tick - yValue) < Y_ZOOM_SLIDER_STEP / 2;
        const pixelsOverlap = hasTickTop ? Math.abs((tickTopPx as number) - topPx) <= Y_AXIS_LABEL_OVERLAP_THRESHOLD_PX : false;
        return valuesMatch || pixelsOverlap;
    });
};
const formatRoundedValue = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '-';
    return formatNumWithKMBAlt(value, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION);
};
const formatRoundedGrowth = (growthPercentage?: number) => {
    if (typeof growthPercentage !== 'number' || Number.isNaN(growthPercentage)) return '-';
    const sign = growthPercentage > 0 ? '+' : growthPercentage < 0 ? '-' : '';
    return `${sign}${formatNumWithKMBAlt(Math.abs(growthPercentage), CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION)}%`;
};
const buildValueAverageLabel = (valueText: string, avgText: string) =>
    `${HEAT_MAP_LABEL_HEADER}\n${valueText} | ${avgText}`;
const getGrowthDirection = (growthPercentage?: number): 'up' | 'down' | 'neutral' => {
    if (typeof growthPercentage !== 'number' || Number.isNaN(growthPercentage)) return 'neutral';
    if (growthPercentage > 0) return 'up';
    if (growthPercentage < 0) return 'down';
    return 'neutral';
};
const getHeatMapBubbleColor = (direction: 'up' | 'down' | 'neutral'): string => {
    if (direction === 'up') return colorConstants.growthUp;
    if (direction === 'down') return colorConstants.growthDown;
    return colorConstants.greyAlt15;
};
/** Figma heat-map segment band uses attention green/red, not the bubble arrow greens. */
const getHeatMapSegmentHighlightColor = (direction: 'up' | 'down' | 'neutral'): string => {
    if (direction === 'up') return '#00A63E';
    if (direction === 'down') return colorConstants.growthDown;
    return colorConstants.greyAlt15;
};
const HEAT_MAP_HIGHLIGHT_OPACITY = 0.07;
const CHART_CONTAINER_ID = 'pack-architecture-bubble-chart-2';
const CHART_SCROLL_CONTAINER_ID = `${CHART_CONTAINER_ID}__scroll`;
const MAX_CANVAS_SURFACE_WIDTH_PX = 30000;
const CHART_PANEL_CLASS = 'rounded-xl border border-[#D9D9D9] bg-white p-4';
const Y_AXIS_LABEL_OVERLAP_THRESHOLD_PX = 14;
const HEAT_MAP_LABEL_HEADER = 'Value   | Avg.';

// @code-guard: skip-next-function reason="Large legacy chart+controls component; scheduled extraction into smaller tokenized sections"
const PackArchitectureChartWithControls: FC<PackArchitectureChartWithControlsProps> = ({
    chartTitle,
    xAxisTitle,
    yAxisTitle,
    chartMinHeightPx,
    horizontalScrollRef: horizontalScrollRefProp,
    isSyncingChartFromTableRef,
    loading = false,
    showError = false,
    errorMessage = 'Insufficient data',
    'data-testid': testId,
}) => {
    const {
        rows,
        bubbleLegend,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleSize,
        bubbleSizeOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        showHeatMap,
        isChartLoading: contextLoading,
        isChartPaginatedLoading,
        isChartPanelLoading,
        isChartError: contextIsError,
        chartError: contextError,
        hasMorePages,
        totalCount,
        fetchNextPricePackArchitecturePage,
        activeSegmentKey,
        selectedColumnKey,
        toggleSegmentSelection,
        selectTableCell,
        clearSelection,
    } = usePackArchitectureContext();
    const {
        allCheckedAttribute,
        timeSelectionLabel,
        timePeriodList,
    } = useContext(FilterContextData);
    const [isLegendExpanded, setIsLegendExpanded] = useState(false);
    const [openOtherBreakdownId, setOpenOtherBreakdownId] = useState<string | null>(null);
    const [visibleChipCount, setVisibleChipCount] = useState(VISIBLE_LEGEND_LIMIT);
    const legendContainerRef = useRef<HTMLDivElement>(null);
    const legendRowRef = useRef<HTMLDivElement>(null);
    const legendMeasureRef = useRef<HTMLDivElement>(null);
    const legendControlsMeasureRef = useRef<HTMLDivElement>(null);
    const legendMoreMeasureRef = useRef<HTMLSpanElement>(null);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const [yAxisBreakpointLabelPositions, setYAxisBreakpointLabelPositions] = useState<YAxisBreakpointLabelPosition[]>([]);
    const [yAxisTickLabelPositions, setYAxisTickLabelPositions] = useState<YAxisTickLabelPosition[]>([]);
    const internalHorizontalScrollRef = useRef<HTMLDivElement | null>(null);
    const horizontalScrollRef = horizontalScrollRefProp ?? internalHorizontalScrollRef;
    const resolvedChartAreaHeightPx = useMemo(() => {
        if (typeof chartMinHeightPx !== 'number' || !Number.isFinite(chartMinHeightPx)) return DEFAULT_CHART_HEIGHT_PX;
        return Math.max(DEFAULT_CHART_HEIGHT_PX, Math.round(chartMinHeightPx));
    }, [chartMinHeightPx]);
    const chartAxisColumnHeightPx = Math.max(0, resolvedChartAreaHeightPx - CHART_AXIS_COLUMN_HEIGHT_OFFSET_PX);
    const chartAxisDividerHeightPx = Math.max(0, resolvedChartAreaHeightPx - CHART_AXIS_DIVIDER_HEIGHT_OFFSET_PX);
    const chartScrollContainerWidth = useContainerWidth({
        containerId: CHART_SCROLL_CONTAINER_ID,
        buffer: 0,
    });
    const isFetchingNextPageRef = useRef(false);
    // Chart height = container height (px), adaptive to screen: measured from the
    // actual rendered chart canvas rather than assumed, so every height-driven
    // bubble-size formula below (radius, Y-axis padding) tracks the real
    // on-screen height instead of drifting from a hardcoded constant.
    const chartCanvasRef = useRef<HTMLDivElement | null>(null);
    const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT_PX);
    useLayoutEffect(() => {
        const node = chartCanvasRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;

        const updateHeight = (measuredHeight: number) => {
            if (measuredHeight > 0) {
                setChartHeight((previous) => (Math.abs(previous - measuredHeight) < 1 ? previous : measuredHeight));
            }
        };

        updateHeight(node.getBoundingClientRect().height);
        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) updateHeight(entry.contentRect.height);
        });
        resizeObserver.observe(node);
        return () => resizeObserver.disconnect();
    }, []);
    const effectiveChartHeightPx = useMemo(
        () => Math.max(DEFAULT_CHART_HEIGHT_PX, resolvedChartAreaHeightPx, chartHeight),
        [chartHeight, resolvedChartAreaHeightPx],
    );
    const bubbleSizing = useMemo(() => computePpaBubbleSizing(effectiveChartHeightPx), [effectiveChartHeightPx]);
    const horizontalScrollRafRef = useRef<number | null>(null);
    const scrollSnapshotBeforePaginationRef = useRef<{ scrollLeft: number; scrollWidth: number } | null>(null);

    // ── Mixpanel tracking refs ──────────────────────────────────────────────
    const chartRenderStartRef = useRef<number>(Date.now());
    const hasTrackedRenderRef = useRef(false);
    const hasTrackedEmptyStateRef = useRef(false);
    const bubbleHoverStartRef = useRef<{
        key: string;
        startedAt: number;
        datum: {
            columnLabel?: string;
            segmentLabel?: string;
            yFieldValue?: number;
        };
    } | null>(null);
    const yZoomApplyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevZoomRef = useRef<[number, number] | null>(null);
    const suppressZoomTrackingUntilRef = useRef(0);
    const chartRows = useMemo<ChartBubbleRow[]>(
        () =>
            (rows ?? []).flatMap((column: PricePackBubbleRow, columnIndex: number) =>
                (column.data ?? []).flatMap((axisItem: AxisGroup) =>
                    (axisItem.data ?? []).map((segment: SegmentItem, segmentIndex: number) => {
                        const yAxisKpi = getKpi(segment, yAxis);
                        const bubbleSizeKpi = getKpi(segment, bubbleSize);
                        const colorKpi = getKpi(segment, 'color_code');
                        const radiusKpi = getKpi(segment, 'radius');
                        const yValue =
                            typeof yAxisKpi?.raw_value === 'number'
                                ? yAxisKpi.raw_value
                                : Number(yAxisKpi?.value ?? 0) || 0;
                        // An unresolved radius (no raw_value from the API) should render at
                        // the minimum size, not a fallback that clamps up to the maximum -
                        // a bubble missing its size KPI should never look like the biggest
                        // bubble on the chart.
                        const radiusValue =
                            typeof radiusKpi?.raw_value === 'number' ? radiusKpi.raw_value : 0;
                        // Backend already normalises this to [0, 1] via the spec's linear
                        // (or sqrt, for outlier handling) interpolation - clamp only, don't
                        // re-apply a sqrt here or the radius scale gets double-transformed.
                        const bubbleSizeValue = Math.max(0, Math.min(1, radiusValue));
                        const selectedGrowthPercentage =
                            typeof bubbleSizeKpi?.growth_percentage === 'number'
                                ? bubbleSizeKpi.growth_percentage
                                : yAxisKpi?.growth_percentage;
                        const growthValue = formatRoundedGrowth(selectedGrowthPercentage);
                        const growthDirection = getGrowthDirection(selectedGrowthPercentage);
                        const bubbleColor = colorKpi?.value || DEFAULT_BUBBLE_COLOR;
                        const xBase = columnIndex + 1;
                        const columnKey = getPackArchitectureEntityKey(column);
                        const segmentKey = getPackArchitectureEntityKey(segment);
                        const valueText = bubbleSizeKpi
                            ? toChartKpiDisplay(bubbleSizeKpi, bubbleSize, yAxis)
                            : formatRoundedValue(radiusValue);
                        const avgText = toChartKpiDisplay(yAxisKpi, yAxis, yAxis);
                        const valueAverageLabel = buildValueAverageLabel(valueText, avgText);
                        const tooltipKpis = (segment.kpis ?? [])
                            .filter((kpi) => !NON_TOOLTIP_KPI_IDS.has(String(kpi.kpi_id).toLowerCase()))
                            .map((kpi) => ({
                                id: kpi.kpi_id,
                                label: kpi.display_label || kpi.kpi_name || kpi.kpi_id,
                                value: toChartKpiDisplay(kpi, kpi.kpi_id, yAxis),
                                growthText: formatRoundedGrowth(kpi.growth_percentage),
                                trendDirection: kpi.trend_direction,
                            }));

                        return {
                            key: `${columnKey}_${axisItem.id}_${segmentKey}_${segmentIndex}`,
                            columnKey,
                            segmentKey,
                            columnLabel: column.description,
                            segmentLabel: segment.description,
                            xFieldValue: xBase,
                            yFieldValue: yValue,
                            bubbleSizeValue,
                            bubbleColor,
                            segmentBaseColor: bubbleColor,
                            valueText,
                            avgText,
                            valueAverageLabel,
                            volumeSalesText: valueText,
                            growthText: growthValue,
                            growthPercentage:
                                typeof selectedGrowthPercentage === 'number' && !Number.isNaN(selectedGrowthPercentage)
                                    ? selectedGrowthPercentage
                                    : null,
                            growthDirection,
                            tooltipKpis,
                            bubbleLabel: '',
                            highlightLabel: '',
                            isPinpointSelected: false,
                        };
                    }),
                ),
            ),
        [bubbleSize, rows, yAxis],
    );
    const activeSelectionMode = useMemo(
        () => resolveActiveSelectionMode(chartRows, activeSegmentKey),
        [activeSegmentKey, chartRows],
    );
    const selectionAggregates = useMemo(() => buildSelectionAggregates(chartRows), [chartRows]);
    const activeSelectionAggregate = useMemo(() => {
        if (!activeSegmentKey || !activeSelectionMode) return null;
        if (activeSelectionMode === 'segment') {
            return selectionAggregates.segmentAggregates.get(activeSegmentKey) ?? null;
        }
        return selectionAggregates.columnAggregates.get(activeSegmentKey) ?? null;
    }, [activeSegmentKey, activeSelectionMode, selectionAggregates]);

    const activeSegmentGrowthMeta = useMemo(() => {
        if (!activeSegmentKey || !showHeatMap || !activeSelectionAggregate) return null;
        if (activeSelectionAggregate.growthCount === 0) {
            return {
                direction: 'neutral' as const,
                color: getHeatMapSegmentHighlightColor('neutral'),
                avgGrowth: null,
                avgGrowthText: '-',
            };
        }

        const avgGrowth = activeSelectionAggregate.sumGrowth / activeSelectionAggregate.growthCount;
        const direction = getGrowthDirection(avgGrowth);

        return {
            direction,
            color: getHeatMapSegmentHighlightColor(direction),
            avgGrowth,
            avgGrowthText: formatRoundedGrowth(avgGrowth),
        };
    }, [activeSegmentKey, activeSelectionAggregate, showHeatMap]);

    const segmentHighlightConfig = useMemo(() => {
        if (!showHeatMap || !activeSegmentKey || !activeSegmentGrowthMeta) return undefined;

        return {
            mode: 'heatMap' as const,
            fillColor: activeSegmentGrowthMeta.color,
            fillOpacity: HEAT_MAP_HIGHLIGHT_OPACITY,
            lineColor: activeSegmentGrowthMeta.color,
            useSegmentAvgLabel: true,
            avgGrowthText: activeSegmentGrowthMeta.avgGrowthText,
        };
    }, [activeSegmentGrowthMeta, activeSegmentKey, showHeatMap]);
    const activeSegmentBreakpoints = useMemo<ActiveSegmentBreakpoints | null>(() => {
        if (!activeSegmentKey || !activeSelectionMode || !activeSelectionAggregate || activeSelectionAggregate.count === 0) {
            return null;
        }

        const avgY = activeSelectionAggregate.sumY / activeSelectionAggregate.count;
        if (segmentHighlightConfig?.mode === 'heatMap') {
            return {
                minY: activeSelectionAggregate.minY,
                maxY: activeSelectionAggregate.maxY,
                avgY,
                segmentColor: segmentHighlightConfig.lineColor,
                regionFillColor: segmentHighlightConfig.fillColor,
                regionFillOpacity: segmentHighlightConfig.fillOpacity,
                useSegmentAvgLabel: Boolean(segmentHighlightConfig.useSegmentAvgLabel),
                avgGrowthText: segmentHighlightConfig.avgGrowthText,
            };
        }

        return {
            minY: activeSelectionAggregate.minY,
            maxY: activeSelectionAggregate.maxY,
            avgY,
            segmentColor: activeSelectionAggregate.segmentBaseColor,
            regionFillColor: activeSelectionAggregate.segmentBaseColor,
            regionFillOpacity: 0.13,
            useSegmentAvgLabel: false,
            avgGrowthText: undefined,
        };
    }, [activeSegmentKey, activeSelectionMode, activeSelectionAggregate, segmentHighlightConfig]);

    const yAxisBounds = useMemo(() => {
        if (chartRows.length === 0) return { min: DEFAULT_Y_MIN, max: DEFAULT_Y_MAX };
        const values = chartRows.map((row) => row.yFieldValue);
        const rawMin = Math.min(...values);
        const rawMax = Math.max(...values);
        if (rawMax <= 0 && rawMin >= 0) return { min: DEFAULT_Y_MIN, max: 1 };
        // Chart always starts at the origin: pin the domain min to 0 instead
        // of padding below the data's own minimum. Genuinely negative data
        // (e.g. a margin/growth KPI) still extends the domain down to cover
        // it - only the common non-negative case (price, sales, volume) gets
        // clamped to exactly 0. Bubble/x-axis clearance at that bottom edge
        // no longer comes from domain padding; it's reserved as a fixed pixel
        // inset on the chart's Y scale `range` instead (see BubbleScatterChart),
        // sized to the largest bubble any row could ever render at, so nothing
        // can overlap the x-axis regardless of how close its value sits to 0.
        const domainMin = Math.min(0, rawMin);
        // Spec: pad the Y domain (not the pixel range) above the data's own
        // max by 0.6 x maxDiameterPx, converted from px to value units, so
        // the largest bubble at the topmost point never gets visually
        // clipped by the plot edge.
        const rawSpan = rawMax - domainMin;
        const targetPaddingPx = bubbleSizing.maxDiameterPx * PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO;
        // `chartHeight` is the whole container, not the actual plot area: G2
        // auto-reserves room outside the plot for its chrome (the known
        // top appendPadding, plus the visible X-axis's line/labels/title -
        // the Y-axis itself is fully hidden here, so it costs nothing). Using
        // the raw container height as the pixel basis below overstates the
        // pixels actually available to the data, so the resulting padding
        // maps to a smaller on-screen gap than intended - correct for that
        // first so the topmost bubble doesn't creep past the plot's top edge.
        const estimatedPlotHeightPx = Math.max(
            0,
            chartHeight - PPA_CHART_TOP_APPEND_PADDING_PX - PPA_CHART_X_AXIS_CHROME_HEIGHT_PX,
        );
        // Solve for padding so the *padded* (final) span maps to exactly
        // targetPaddingPx on screen - estimating px-per-unit off the unpadded
        // span alone understates it, since adding padding grows the span and
        // dilutes px-per-unit, silently shrinking the actual on-screen margin.
        // Only the top needs this value-domain padding now (the bottom is
        // pinned to domainMin exactly), so the full available budget goes to it.
        const availablePxForData = estimatedPlotHeightPx - targetPaddingPx;
        const paddingValueUnits =
            rawSpan > 0 && availablePxForData > 0
                ? (targetPaddingPx * rawSpan) / availablePxForData
                : Math.max(1, Math.abs(rawMax) * 0.1);
        return {
            min: domainMin,
            max: rawMax + paddingValueUnits,
        };
    }, [bubbleSizing.maxDiameterPx, chartHeight, chartRows]);

    const isYZoomDisabled = chartRows.length <= 1;

    const [yZoomRange, setYZoomRange] = useState<[number, number]>([yAxisBounds.min, yAxisBounds.max]);
    const [yZoomSliderRange, setYZoomSliderRange] = useState<[number, number]>([yAxisBounds.min, yAxisBounds.max]);

    const applyYZoomRangeImmediate = useCallback((nextRange: [number, number]) => {
        if (yZoomApplyDebounceRef.current) {
            clearTimeout(yZoomApplyDebounceRef.current);
            yZoomApplyDebounceRef.current = null;
        }
        suppressZoomTrackingUntilRef.current = Date.now() + 150;
        prevZoomRef.current = nextRange;
        setYZoomSliderRange(nextRange);
        setYZoomRange(nextRange);
    }, []);

    const handleYZoomRangeChange = useCallback((nextRange: [number, number]) => {
        setYZoomSliderRange(nextRange);
        if (yZoomApplyDebounceRef.current) clearTimeout(yZoomApplyDebounceRef.current);
        yZoomApplyDebounceRef.current = setTimeout(() => {
            setYZoomRange(nextRange);
            yZoomApplyDebounceRef.current = null;
        }, Y_ZOOM_DRAG_DEBOUNCE_MS);
    }, []);

    const handleYZoomRangeCommit = useCallback((nextRange: [number, number]) => {
        if (Date.now() < suppressZoomTrackingUntilRef.current) {
            prevZoomRef.current = nextRange;
            return;
        }

        const prev = prevZoomRef.current ?? [yAxisBounds.min, yAxisBounds.max];
        if (!shouldTrackYZoomChange(prev, nextRange, yAxisBounds)) {
            prevZoomRef.current = nextRange;
            return;
        }

        trackEventMixpanel(PPA_PACK_ARCH_ZOOM_CHANGED, {
            previous_min: prev[0],
            previous_max: prev[1],
            selected_min: nextRange[0],
            selected_max: nextRange[1],
            zoom_percentage: getYZoomPercentage(
                nextRange[0],
                nextRange[1],
                yAxisBounds.min,
                yAxisBounds.max,
            ),
        });
        prevZoomRef.current = nextRange;
    }, [yAxisBounds]);

    useEffect(() => {
        applyYZoomRangeImmediate([yAxisBounds.min, yAxisBounds.max]);
    }, [applyYZoomRangeImmediate, yAxisBounds.max, yAxisBounds.min]);

    useEffect(() => {
        if (!isYZoomDisabled) return;
        applyYZoomRangeImmediate([yAxisBounds.min, yAxisBounds.max]);
    }, [applyYZoomRangeImmediate, isYZoomDisabled, yAxisBounds.max, yAxisBounds.min]);

    useEffect(() => {
        if (!selectedColumnKey || !horizontalScrollRef.current) return;

        const columnIndex = (rows ?? []).findIndex((row) => getPackArchitectureEntityKey(row) === selectedColumnKey);
        if (columnIndex === -1) return;

        const container = horizontalScrollRef.current;
        const columnPitchPx = bubbleSizing.columnWidthPx + bubbleSizing.columnGapPx;
        const targetLeft = Math.max(0, columnIndex * columnPitchPx - container.clientWidth / 2);
        container.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }, [bubbleSizing.columnGapPx, bubbleSizing.columnWidthPx, rows, selectedColumnKey]);

    useEffect(() => {
        if (!activeSegmentKey) return;
        applyYZoomRangeImmediate([yAxisBounds.min, yAxisBounds.max]);
    }, [activeSegmentKey, applyYZoomRangeImmediate, selectedColumnKey, yAxisBounds.max, yAxisBounds.min]);

    // ── Mixpanel: chart_rendered (fire once after first data arrives) ───────
    useEffect(() => {
        if (hasTrackedRenderRef.current || contextLoading || rows.length === 0) return;
        hasTrackedRenderRef.current = true;
        trackEventMixpanel(PPA_PACK_ARCH_CHART_RENDERED, {
            x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
            y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
            bubble_represents: getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
            bubble_size: getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
            heatmap_enabled: showHeatMap,
            render_time_ms: Date.now() - chartRenderStartRef.current,
        });
    }, [
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        bubbleSize,
        bubbleSizeOptionGroups,
        contextLoading,
        rows.length,
        showHeatMap,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
    ]);

    const regularSegmentLegends = useMemo<SegmentLegend[]>(() => {
        // PA-03: prefer the backend's ranked bubble_legend (sorted by the
        // bubble-size KPI, top 80 individually coloured + one trailing
        // aggregated "Other {Dimension}" entry) - it is complete and stable
        // regardless of which X-axis pages have loaded. Falls back to the
        // legacy first-seen-order derivation from loaded chartRows for
        // response shapes that don't carry a bubble_legend (e.g. Case B
        // binned-KPI X-axis, or no bubble-represents dimension selected).
        if (bubbleLegend && bubbleLegend.length > 0) {
            return bubbleLegend
                .slice()
                .sort((a, b) => {
                    if (a.is_other !== b.is_other) return a.is_other ? 1 : -1;
                    return (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER);
                })
                .map((entry) => ({
                    id: entry.ex_id,
                    label: entry.description,
                    color: entry.color_code,
                    isOther: entry.is_other,
                    otherMembers: entry.other_members,
                }));
        }
        const map = new Map<string, { label: string; color: string }>();
        chartRows.forEach((row) => {
            if (!map.has(row.segmentKey)) {
                map.set(row.segmentKey, {
                    label: row.segmentLabel,
                    color: row.segmentBaseColor,
                });
            }
        });
        return Array.from(map.entries())
            .map(([id, { label, color }]) => ({ id, label, color, isOther: isOtherLikeLabel(label) }))
            .sort((a, b) => {
                if (a.isOther === b.isOther) return 0;
                return a.isOther ? 1 : -1;
            });
    }, [bubbleLegend, chartRows]);

    // The `__other__` ("Other Vendor") x-axis column is intentionally excluded from the
    // legend - it should not appear as a chip alongside the regular segment legends.
    const allSegmentLegends = regularSegmentLegends;

    const visibleSegments = useMemo(() => {
        const limit = Math.min(visibleChipCount, allSegmentLegends.length);
        const defaultVisibleSegments = allSegmentLegends.slice(0, limit);
        if (!activeSegmentKey) return defaultVisibleSegments;

        const activeIndex = allSegmentLegends.findIndex((segment) => segment.id === activeSegmentKey);
        if (activeIndex === -1 || activeIndex < limit) return defaultVisibleSegments;

        const activeSegment = allSegmentLegends[activeIndex];
        // "Other"-like segments always live at the end; never evict a regular chip to show them.
        if (activeSegment.isOther ?? isOtherLikeLabel(activeSegment.label)) return defaultVisibleSegments;

        const visibleWithoutLast = defaultVisibleSegments.slice(0, Math.max(0, limit - 1));
        return [...visibleWithoutLast, activeSegment];
    }, [activeSegmentKey, allSegmentLegends, visibleChipCount]);

    const hasLegendOverflow = allSegmentLegends.length > visibleChipCount;
    const hiddenLegendCount = Math.max(0, allSegmentLegends.length - visibleChipCount);

    useLayoutEffect(() => {
        if (isLegendExpanded) return undefined;

        const container = legendContainerRef.current;
        const measure = legendMeasureRef.current;
        if (!container || !measure || allSegmentLegends.length === 0) {
            setVisibleChipCount((previous) =>
                previous === allSegmentLegends.length ? previous : allSegmentLegends.length,
            );
            return undefined;
        }

        const updateVisibleChipCount = () => {
            const chips = measure.querySelectorAll('[data-legend-chip]');
            const chipWidths = Array.from(chips).map((chip) => (chip as HTMLElement).offsetWidth);
            const moreLabelWidth = legendMoreMeasureRef.current?.offsetWidth ?? LEGEND_MORE_LABEL_WIDTH_PX;
            const dropdownWidth = legendControlsMeasureRef.current?.querySelector('[data-legend-dropdown-measure]')
                ? (legendControlsMeasureRef.current.querySelector('[data-legend-dropdown-measure]') as HTMLElement)
                      .offsetWidth
                : LEGEND_DROPDOWN_BUTTON_WIDTH_PX;
            const reservedRightWidth =
                (legendControlsMeasureRef.current?.offsetWidth ??
                    moreLabelWidth + dropdownWidth + LEGEND_RIGHT_CONTROLS_GAP_PX) + LEGEND_RIGHT_CONTROLS_GAP_PX;
            const availableChipWidth = Math.max(0, container.clientWidth - reservedRightWidth);
            const nextCount = countLegendChipsThatFit(chipWidths, availableChipWidth, 0);
            const boundedCount = Math.min(nextCount, VISIBLE_LEGEND_LIMIT, allSegmentLegends.length);
            setVisibleChipCount((previous) => (previous === boundedCount ? previous : boundedCount));
        };

        updateVisibleChipCount();

        const resizeObserver = new ResizeObserver(updateVisibleChipCount);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [isLegendExpanded, allSegmentLegends]);

    useEffect(() => {
        setIsLegendExpanded(false);
        setOpenOtherBreakdownId(null);
    }, [allSegmentLegends.length]);

    const toggleActiveSegment = useCallback(
        (segmentKey: string) => {
            const isDeselect = activeSegmentKey === segmentKey && !selectedColumnKey;
            toggleSegmentSelection(segmentKey);
            if (isDeselect) return;

            const segment = allSegmentLegends.find((s) => s.id === segmentKey);
            if (showHeatMap && segment) {
                trackEventMixpanel(PPA_PACK_ARCH_HEATMAP_FILTERED_VIEW_USED, {
                    selected_legend_value: segment.label,
                });
            }
            trackEventMixpanel(PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED, {
                selection_source: 'legend',
                bubble_represents: getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
                bubble_represents_value: segment?.label ?? segmentKey,
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
            });
        },
        [
            activeSegmentKey,
            allSegmentLegends,
            bubbleRepresents,
            bubbleRepresentsOptionGroups,
            selectedColumnKey,
            showHeatMap,
            toggleSegmentSelection,
            xAxis,
            xAxisOptionGroups,
            yAxis,
            yAxisOptionGroups,
        ],
    );

    const handleBubbleClick = useCallback(
        (datum: { key?: string; segmentKey?: string; columnKey?: string }) => {
            if (!datum.segmentKey || !datum.columnKey) return;
            // Clicking a bubble should highlight its whole segment across
            // every vendor - the same segment-wide highlight/avg-range-band
            // behaviour as clicking that segment's cell in the table -
            // rather than spotlighting only the single bubble that was
            // clicked, which is what routing this through `selectBubble`
            // (bubble-only pinpoint selection) used to do.
            selectTableCell(datum.segmentKey, datum.columnKey);

            const isDeselect = activeSegmentKey === datum.segmentKey && selectedColumnKey === datum.columnKey;
            if (isDeselect) return;

            const bubbleRow = datum.key ? chartRows.find((row) => row.key === datum.key) : undefined;
            trackEventMixpanel(PPA_PACK_ARCH_BUBBLE_OR_CELL_SELECTED, {
                selection_source: 'bubble',
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
                bubble_represents: getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
                bubble_represents_value: bubbleRow?.segmentLabel ?? datum.segmentKey,
                x_axis_value: bubbleRow?.columnLabel ?? datum.columnKey,
                y_axis_value: bubbleRow?.yFieldValue,
            });
        },
        [
            activeSegmentKey,
            bubbleRepresents,
            bubbleRepresentsOptionGroups,
            chartRows,
            selectTableCell,
            selectedColumnKey,
            xAxis,
            xAxisOptionGroups,
            yAxisOptionGroups,
            yAxis,
        ],
    );

    const trackBubbleHoverIfEligible = useCallback(
        (hover: NonNullable<typeof bubbleHoverStartRef.current>, endedAt = Date.now()) => {
            const hoverDurationMs = endedAt - hover.startedAt;
            if (hoverDurationMs <= BUBBLE_HOVER_TRACK_MIN_MS) return;

            trackEventMixpanel(PPA_PACK_ARCH_BUBBLE_HOVER, {
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                x_axis_value: hover.datum.columnLabel ?? '',
                bubble_represents_value: hover.datum.segmentLabel ?? '',
                y_axis_metric: getAxisLabel(yAxis, yAxisOptionGroups),
                y_axis_value: hover.datum.yFieldValue,
                bubble_size_metric: getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
                hover_duration: hoverDurationMs,
            });
        },
        [bubbleSize, bubbleSizeOptionGroups, xAxis, xAxisOptionGroups, yAxis, yAxisOptionGroups],
    );

    const handleBubbleHover = useCallback(
        (datum: {
            key?: string;
            columnLabel?: string;
            segmentLabel?: string;
            yFieldValue?: number;
            volumeSalesText?: string;
        }) => {
            const bubbleKey = datum.key;
            if (!bubbleKey) return;

            const activeHover = bubbleHoverStartRef.current;
            if (activeHover?.key === bubbleKey) return;
            if (activeHover) {
                trackBubbleHoverIfEligible(activeHover);
            }

            bubbleHoverStartRef.current = {
                key: bubbleKey,
                startedAt: Date.now(),
                datum: {
                    columnLabel: datum.columnLabel,
                    segmentLabel: datum.segmentLabel,
                    yFieldValue: datum.yFieldValue,
                },
            };
        },
        [trackBubbleHoverIfEligible],
    );

    const handleBubbleHoverEnd = useCallback(() => {
        const activeHover = bubbleHoverStartRef.current;
        bubbleHoverStartRef.current = null;
        if (!activeHover) return;
        trackBubbleHoverIfEligible(activeHover);
    }, [trackBubbleHoverIfEligible]);

    // Short prefix for the bubble info popup's value row - always the metric's
    // first word (e.g. "Volume Sales" -> "Volume", "Value Sales ($)" -> "Value")
    // since the popup has no horizontal room for the full metric name.
    const bubbleSizeValueLabel = useMemo(
        () => getAxisLabel(bubbleSize, bubbleSizeOptionGroups).split(' ')[0] || 'Value',
        [bubbleSize, bubbleSizeOptionGroups],
    );

    const chartDataset = useMemo(
        () =>
            chartRows
                .filter((point) => point.yFieldValue >= yZoomRange[0] && point.yFieldValue <= yZoomRange[1])
                .map((point) => {
                    const isActiveSegment =
                        Boolean(activeSegmentKey) &&
                        Boolean(activeSelectionMode) &&
                        (activeSelectionMode === 'segment'
                            ? point.segmentKey === activeSegmentKey
                            : point.columnKey === activeSegmentKey);
                    const shouldShowDetailedInfoLabel = isActiveSegment;
                    const shouldShowGrowthPctLabel = showHeatMap && !activeSegmentKey && point.growthText !== '-';
                    // A cell click (segment + column both known) pinpoints exactly one
                    // bubble - give it the same hover-style outline/shadow so it's clear
                    // which bubble the selected table cell corresponds to, layered on top
                    // of the segment-wide highlight every bubble in that segment already
                    // gets from `isActiveSegment` above.
                    const isSelectedCell = Boolean(
                        activeSegmentKey &&
                            selectedColumnKey &&
                            point.segmentKey === activeSegmentKey &&
                            point.columnKey === selectedColumnKey,
                    );
                    return {
                        ...point,
                        name: point.segmentLabel,
                        vendor: point.columnLabel,
                        segment: point.segmentLabel,
                        segmentKey: point.segmentKey,
                        columnKey: point.columnKey,
                        segmentBaseColor: point.segmentBaseColor,
                        highlightLabel: isActiveSegment ? point.segmentLabel : '',
                        bubbleColor: showHeatMap ? getHeatMapBubbleColor(point.growthDirection) : point.bubbleColor,
                        bubbleLabel: '',
                        isPinpointSelected: isSelectedCell,
                        isPinpointDimmed: false,
                        showBubbleInfoLabel: shouldShowDetailedInfoLabel,
                        showGrowthPctLabel: shouldShowGrowthPctLabel,
                        infoLabelValueText: point.volumeSalesText,
                        infoLabelValueLabel: bubbleSizeValueLabel,
                        infoLabelMetric: showHeatMap && isActiveSegment ? 'Growth' : 'Avg.',
                        infoLabelMetricValue: showHeatMap && isActiveSegment ? point.growthText : point.avgText,
                        infoLabelMetricDirection: point.growthDirection,
                    };
                }),
        [activeSegmentKey, activeSelectionMode, bubbleSizeValueLabel, chartRows, selectedColumnKey, showHeatMap, yZoomRange],
    );

    const xTickLabels = useMemo(() => (rows ?? []).map((row: PricePackBubbleRow) => row.description), [rows]);
    const yAxisTickValues = useMemo(() => {
        const [rangeMin, rangeMax] = yZoomRange;
        if (!Number.isFinite(rangeMin) || !Number.isFinite(rangeMax) || rangeMax <= rangeMin) {
            return [rangeMin, rangeMax];
        }
        const step = (rangeMax - rangeMin) / (Y_AXIS_TICK_COUNT - 1);
        return Array.from({ length: Y_AXIS_TICK_COUNT }, (_, index) =>
            Number((rangeMin + step * index).toFixed(CHART_DECIMAL_PRECISION)),
        );
    }, [yZoomRange]);
    const yAxisTickLabelsDescending = useMemo(() => [...yAxisTickValues].reverse(), [yAxisTickValues]);
    const yAxisTickTopByKey = useMemo(() => {
        const topByKey = new Map<string, number>();
        yAxisTickLabelPositions.forEach(({ yValue, topPx }) => {
            if (!Number.isFinite(topPx)) return;
            topByKey.set(getYAxisTickPositionKey(yValue), topPx);
        });
        return topByKey;
    }, [yAxisTickLabelPositions]);
    const minScrollableChartWidth = useMemo(() => {
        const numColumns = xTickLabels.length;
        const viewportWidth =
            typeof chartScrollContainerWidth === 'number' && Number.isFinite(chartScrollContainerWidth)
                ? chartScrollContainerWidth
                : 0;
        if (numColumns === 0) return Math.max(bubbleSizing.columnWidthPx, viewportWidth);
        // Spec: totalWidth = (columnWidth x numColumns) + (columnGap x (numColumns - 1)).
        const contentWidth =
            numColumns * bubbleSizing.columnWidthPx + Math.max(0, numColumns - 1) * bubbleSizing.columnGapPx;
        return Math.max(contentWidth, viewportWidth);
    }, [bubbleSizing.columnGapPx, bubbleSizing.columnWidthPx, chartScrollContainerWidth, xTickLabels.length]);
    const chartCanvasWidth = useMemo(
        () => `max(calc(100% + ${MIN_INITIAL_SCROLLABLE_EXTRA_WIDTH_PX}px), ${minScrollableChartWidth}px)`,
        [minScrollableChartWidth],
    );
    const chartChunks = useMemo(() => {
        const columnPitchPx = Math.max(1, bubbleSizing.columnWidthPx + bubbleSizing.columnGapPx);
        const maxColumnsPerChunk = Math.max(1, Math.floor(MAX_CANVAS_SURFACE_WIDTH_PX / columnPitchPx));
        const totalColumns = xTickLabels.length;

        if (totalColumns === 0) {
            return [
                {
                    chartContainerId: `${CHART_CONTAINER_ID}__chunk_0`,
                    xTickLabels: [] as string[],
                    xRange: { xMin: 1, xMax: 1 },
                    widthPx: bubbleSizing.columnWidthPx,
                    dataset: chartDataset,
                },
            ];
        }

        const chunks: Array<{
            chartContainerId: string;
            xTickLabels: string[];
            xRange: { xMin: number; xMax: number };
            widthPx: number;
            dataset: Array<Record<string, unknown>>;
        }> = [];

        for (let start = 0; start < totalColumns; start += maxColumnsPerChunk) {
            const endExclusive = Math.min(totalColumns, start + maxColumnsPerChunk);
            const labels = xTickLabels.slice(start, endExclusive);
            const chunkContentWidthPx =
                labels.length * bubbleSizing.columnWidthPx + Math.max(0, labels.length - 1) * bubbleSizing.columnGapPx;
            const isOnlyChunk = start === 0 && endExclusive === totalColumns;
            const widthPx = isOnlyChunk ? Math.max(chunkContentWidthPx, minScrollableChartWidth) : chunkContentWidthPx;
            const dataset = chartDataset
                .filter((point) => {
                    const xValue = Number((point as { xFieldValue?: unknown }).xFieldValue);
                    return Number.isFinite(xValue) && xValue >= start + 1 && xValue <= endExclusive;
                })
                .map((point) => {
                    const xValue = Number((point as { xFieldValue?: unknown }).xFieldValue);
                    return {
                        ...point,
                        xFieldValue: xValue - start,
                    };
                });

            chunks.push({
                chartContainerId: `${CHART_CONTAINER_ID}__chunk_${start}`,
                xTickLabels: labels,
                xRange: { xMin: 1, xMax: Math.max(1, labels.length) },
                widthPx,
                dataset,
            });
        }

        return chunks;
    }, [
        bubbleSizing.columnGapPx,
        bubbleSizing.columnWidthPx,
        chartDataset,
        minScrollableChartWidth,
        xTickLabels,
    ]);

    const shouldShowError = showError || ((contextIsError || !!contextError) && !contextLoading);
    const showPaginationLoader = isFetchingNextPage || isChartPaginatedLoading;
    const showPanelLoader = (loading || isChartPanelLoading) && !showPaginationLoader;

    const handleHorizontalScrollNow = useCallback(() => {
        const container = horizontalScrollRef.current;
        if (!container || contextLoading) return;

        const { scrollLeft, clientWidth, scrollWidth } = container;
        const totalColumns = rows.length;

        if (!hasMorePages || isFetchingNextPageRef.current) return;

        const reachedExtremeRight =
            scrollLeft + clientWidth >= scrollWidth - PAGINATION_RIGHT_EDGE_THRESHOLD_PX;
        if (!reachedExtremeRight) return;

        scrollSnapshotBeforePaginationRef.current = { scrollLeft, scrollWidth };
        isFetchingNextPageRef.current = true;
        setIsFetchingNextPage(true);

        void fetchNextPricePackArchitecturePage()
            .then((response) => {
                if (!response) return;
                const columnsLoaded = totalColumns + (response.data?.length ?? 0);
                const totalColumnsCount = response.total_count ?? totalCount ?? columnsLoaded;
                trackEventMixpanel(PPA_PACK_ARCH_CHART_LAZY_LOAD_TRIGGERED, {
                    columns_loaded: columnsLoaded,
                    total_columns: totalColumnsCount,
                    scroll_depth_pct: totalColumnsCount > 0 ? Math.round((columnsLoaded / totalColumnsCount) * 100) : 100,
                });
            })
            .finally(() => {
                isFetchingNextPageRef.current = false;
                setIsFetchingNextPage(false);
            });
    }, [contextLoading, fetchNextPricePackArchitecturePage, hasMorePages, rows.length, totalCount]);

    const handleHorizontalScroll = useCallback(() => {
        if (horizontalScrollRafRef.current !== null) return;
        horizontalScrollRafRef.current = requestAnimationFrame(() => {
            horizontalScrollRafRef.current = null;
            handleHorizontalScrollNow();
        });
    }, [handleHorizontalScrollNow]);

    useLayoutEffect(() => {
        const snapshot = scrollSnapshotBeforePaginationRef.current;
        const container = horizontalScrollRef.current;
        if (!snapshot || !container) return;

        const widthGrowth = container.scrollWidth - snapshot.scrollWidth;
        const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        const targetScrollLeft =
            widthGrowth > 0
                ? snapshot.scrollLeft
                : snapshot.scrollLeft - PAGINATION_SCROLL_RETREAT_PX;
        container.scrollLeft = Math.min(maxScrollLeft, Math.max(0, targetScrollLeft));
        scrollSnapshotBeforePaginationRef.current = null;
        container.dispatchEvent(new Event('scroll'));
    }, [rows.length]);

    useEffect(() => {
        return () => {
            if (horizontalScrollRafRef.current !== null) {
                cancelAnimationFrame(horizontalScrollRafRef.current);
                horizontalScrollRafRef.current = null;
            }
            if (yZoomApplyDebounceRef.current) {
                clearTimeout(yZoomApplyDebounceRef.current);
                yZoomApplyDebounceRef.current = null;
            }
        };
    }, []);

    if (shouldShowError) {
        return (
            <div className={`relative ${CHART_PANEL_CLASS}`} data-testid={testId ?? PPA_PACK_ARCH_CHART_ERROR}>
                <div
                    className="flex items-center justify-center"
                    style={{ height: effectiveChartHeightPx }}
                    data-testid={PPA_PACK_ARCH_CHART_ERROR}
                >
                    <ErrorBox msg={contextError || errorMessage} />
                </div>
            </div>
        );
    }

    if (!showPanelLoader && (chartRows?.length ?? 0) === 0) {
        if (!hasTrackedEmptyStateRef.current) {
            hasTrackedEmptyStateRef.current = true;
            const globalFilters = buildPackArchitectureGlobalFiltersSummary({
                timeSelectionLabel,
                chartRequest: null,
                timePeriodList,
                allCheckedAttribute,
            });
            trackEventMixpanel(PPA_PACK_ARCH_EMPTY_STATE_VIEWED, {
                x_axis: getAxisLabel(xAxis, xAxisOptionGroups),
                y_axis: getAxisLabel(yAxis, yAxisOptionGroups),
                bubble_represents: getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
                bubble_size: getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
                error_message: errorMessage || 'No data available',
                global_filters: globalFilters || '-',
            });
        }
        return (
            <div className={`relative ${CHART_PANEL_CLASS}`} data-testid={testId ?? PPA_PACK_ARCH_CHART_EMPTY}>
                <h3 className="mb-3 text-[18px] font-normal leading-[34px] text-headerColor">{chartTitle}</h3>
                <div
                    className="flex flex-col items-center justify-center gap-2"
                    style={{ height: effectiveChartHeightPx }}
                    data-testid={PPA_PACK_ARCH_CHART_EMPTY}
                >
                    <div className="flex h-[61px] w-[96px] shrink-0 items-center justify-center">
                        <InboxOutlined customIndex={0} />
                    </div>
                    <div className="flex flex-col items-center gap-1 text-center">
                        <p className="m-0 text-base font-normal leading-[22px] text-black/45">
                            No data for selected configuration
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${CHART_PANEL_CLASS}`} data-testid={testId}>
            {showPanelLoader ? (
                <div
                    className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80"
                    data-testid={PPA_PACK_ARCH_CHART_LOADING}
                >
                    <Loader />
                </div>
            ) : null}
            <h3 className="mb-3 text-[18px] font-normal leading-[34px] text-headerColor">{chartTitle}</h3>

            {(rows?.length ?? 0) === 0 ? (
                <div style={{ height: effectiveChartHeightPx }} />
            ) : (
                <>
                    <div
                        ref={legendContainerRef}
                        className={`mb-4 flex gap-2 ${isLegendExpanded ? 'items-start' : 'items-center'}`}
                    >
                        <div className="relative min-w-0 min-h-6 flex-1">
                            {!isLegendExpanded ? (
                                <>
                                    <div
                                        ref={legendMeasureRef}
                                        className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-3 opacity-0"
                                        aria-hidden
                                    >
                                        {allSegmentLegends.map((segment) => (
                                            <LegendChip
                                                key={`measure-${segment.id}`}
                                                segment={segment}
                                                isActive={false}
                                                isDimmed={false}
                                                measureOnly
                                            />
                                        ))}
                                    </div>
                                    <div
                                        ref={legendControlsMeasureRef}
                                        className="pointer-events-none absolute right-0 top-0 flex items-center gap-2 opacity-0"
                                        aria-hidden
                                    >
                                        <span
                                            ref={legendMoreMeasureRef}
                                            className="inline-flex shrink-0 whitespace-nowrap text-xs text-toolbarLabelGrey"
                                        >
                                            +99 more
                                        </span>
                                        <span
                                            data-legend-dropdown-measure
                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#E4E4E7] bg-white p-0"
                                        >
                                            <HeaderSortDescChevron color={colorConstants.blackTitle} rotate={0} />
                                        </span>
                                    </div>
                                </>
                            ) : null}

                            <div
                                ref={legendRowRef}
                                className={
                                    isLegendExpanded
                                        ? 'flex min-h-6 flex-wrap items-start gap-3 overflow-y-auto overflow-x-hidden pb-1'
                                        : 'flex min-h-6 items-center gap-3 overflow-hidden'
                                }
                                style={isLegendExpanded ? { maxHeight: LEGEND_EXPANDED_MAX_HEIGHT_PX } : undefined}
                            >
                                {(isLegendExpanded ? allSegmentLegends : visibleSegments).map((segment) => {
                                    const isActive = activeSegmentKey === segment.id;
                                    const isDimmed = activeSegmentKey !== null && !isActive;
                                    const isBreakdownOpen = openOtherBreakdownId === segment.id;
                                    return (
                                        <Fragment key={segment.id}>
                                            <LegendChip
                                                segment={segment}
                                                isActive={isActive}
                                                isDimmed={isDimmed}
                                                truncate={!isLegendExpanded}
                                                onClick={() => toggleActiveSegment(segment.id)}
                                                isBreakdownOpen={isBreakdownOpen}
                                                onToggleBreakdown={() => {
                                                    const isOpening = openOtherBreakdownId !== segment.id;
                                                    if (!isLegendExpanded) setIsLegendExpanded(true);
                                                    setOpenOtherBreakdownId((prev) => (prev === segment.id ? null : segment.id));
                                                    if (!isOpening) return;
                                                    trackEventMixpanel(PPA_PACK_ARCH_LEGEND_OTHER_EXPANDED, {
                                                        bubble_represents_dimension: getAxisLabel(
                                                            bubbleRepresents,
                                                            bubbleRepresentsOptionGroups,
                                                        ),
                                                        grouped_item_count: segment.otherMembers?.length ?? 0,
                                                    });
                                                }}
                                            />
                                            {isBreakdownOpen && segment.otherMembers?.length ? (
                                                <OtherLegendBreakdownContent
                                                    otherMembers={segment.otherMembers}
                                                    color={segment.color}
                                                />
                                            ) : null}
                                        </Fragment>
                                    );
                                })}
                            </div>
                        </div>

                        {hasLegendOverflow ? (
                            <div className="flex h-6 shrink-0 items-center gap-2">
                                {!isLegendExpanded ? (
                                    <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap text-xs leading-none text-toolbarLabelGrey">
                                        {`+${hiddenLegendCount} more`}
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border border-borderGreyAl1 bg-transparent p-0 shadow-none transition-colors duration-150 hover:border-purpleFontSelected hover:bg-transparent active:border-purpleFontPressed"
                                    onClick={() => {
                                        const expanding = !isLegendExpanded;
                                        setIsLegendExpanded(expanding);
                                        if (expanding) {
                                            trackEventMixpanel(PPA_PACK_ARCH_LEGEND_EXPANDED, {
                                                bubble_represents_dimension: bubbleRepresents,
                                                hidden_item_count: hiddenLegendCount,
                                            });
                                        }
                                    }}
                                    aria-expanded={isLegendExpanded}
                                    aria-label={isLegendExpanded ? 'Collapse legend' : 'Expand legend'}
                                    data-testid={PPA_PACK_ARCH_LEGEND_EXPAND_BTN}
                                >
                                    <HeaderSortDescChevron
                                        color={colorConstants.blackTitle}
                                        rotate={isLegendExpanded ? 180 : 0}
                                    />
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <div className="relative flex gap-0" style={{ height: effectiveChartHeightPx }}>
                        {showPaginationLoader ? (
                            <div
                                className="absolute inset-0 z-20 flex cursor-wait items-center justify-center bg-white/45"
                                aria-live="polite"
                                aria-label="Loading more chart data"
                                aria-busy="true"
                            >
                                <Spin size="small" />
                            </div>
                        ) : null}
                        <div
                            className="sticky left-0 z-10 flex shrink-0 items-stretch gap-2 bg-white pr-0"
                            style={{ height: chartAxisColumnHeightPx }}
                        >
                            <VerticalRangeSlider
                                min={yAxisBounds.min}
                                max={yAxisBounds.max}
                                value={yZoomSliderRange}
                                onChange={handleYZoomRangeChange}
                                onAfterChange={handleYZoomRangeCommit}
                                formatLabel={formatRangeLabel}
                                step={Y_ZOOM_SLIDER_STEP}
                                disabled={isYZoomDisabled}
                            />
                            <div className="flex h-full w-[18px] items-center justify-center">
                                <span className="-rotate-90 whitespace-nowrap text-[12px] font-normal leading-[18px] text-[#71717B]">
                                    {yAxisTitle}
                                </span>
                            </div>
                            <div className="relative h-full w-[48px] shrink-0 overflow-visible">
                                {yAxisBreakpointLabelPositions.length > 0 ? (
                                    <div className="pointer-events-none absolute inset-0 z-[1] overflow-visible">
                                        {yAxisBreakpointLabelPositions.map(({ label, topPx, color }) => (
                                            <span
                                                key={`${label}-${topPx}`}
                                                className="absolute right-0 -translate-y-1/2 whitespace-nowrap text-right text-[10px] italic font-semibold leading-none"
                                                style={{ top: topPx, color: color ?? '#18181B' }}
                                            >
                                                {label}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                                <div className="relative h-full w-full py-[8px] text-right">
                                    {yAxisTickLabelsDescending.map((tick, index) => {
                                        const tickTopPx = yAxisTickTopByKey.get(getYAxisTickPositionKey(tick));
                                        if (typeof tickTopPx !== 'number' || !Number.isFinite(tickTopPx)) return null;
                                        const isHiddenByBreakpoint = doesYAxisTickOverlapBreakpoint(
                                            tick,
                                            tickTopPx,
                                            yAxisBreakpointLabelPositions,
                                        );

                                        return (
                                            <span
                                                key={`y-tick-${index}-${tick}`}
                                                className={`absolute right-0 -translate-y-1/2 whitespace-nowrap text-[12px] font-normal leading-[18px] text-[#71717B] ${
                                                    isHiddenByBreakpoint ? 'invisible' : ''
                                                }`}
                                                style={{ top: tickTopPx }}
                                            >
                                                {formatRangeLabel(tick)}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                            <div
                                className="w-px shrink-0 self-start bg-[#D9D9D9]"
                                style={{ height: chartAxisDividerHeightPx }}
                                aria-hidden
                            />
                        </div>

                        <div
                            id={CHART_SCROLL_CONTAINER_ID}
                            ref={horizontalScrollRef as RefObject<HTMLDivElement>}
                            onScroll={handleHorizontalScroll}
                            className="relative min-w-0 flex-1 overflow-x-auto overflow-y-hidden rounded-xl bg-white"
                        >
                            <div
                                ref={chartCanvasRef}
                                className="relative h-full flex flex-row flex-nowrap"
                                style={{ width: chartCanvasWidth, height: effectiveChartHeightPx }}
                            >
                                {chartChunks.map((chunk, index) => (
                                    <div key={chunk.chartContainerId} className="h-full shrink-0" style={{ width: `${chunk.widthPx}px` }}>
                                        <BubbleScatterChart
                                            chartContainer={chunk.chartContainerId}
                                            height={effectiveChartHeightPx}
                                            dataset={chunk.dataset}
                                            xAxisTitle={xAxisTitle}
                                            yAxisTitle={yAxisTitle}
                                            stroke="#d9d9d9"
                                            xRange={chunk.xRange}
                                            yRange={{ xMin: yZoomRange[0], xMax: yZoomRange[1] }}
                                            yTickValues={yAxisTickValues}
                                            hideYAxisLabels
                                            hideYAxisTitle
                                            hideYAxisLine
                                            xTickLabels={chunk.xTickLabels}
                                            activeSegmentId={activeSegmentKey}
                                            segmentHighlightConfig={segmentHighlightConfig}
                                            activeSegmentBreakpointsOverride={activeSegmentBreakpoints}
                                            onBubbleClick={handleBubbleClick}
                                            onBubbleHover={handleBubbleHover}
                                            onBubbleHoverEnd={handleBubbleHoverEnd}
                                            onChartBackgroundClick={clearSelection}
                                            onYAxisBreakpointLabelPositions={
                                                index === 0 ? setYAxisBreakpointLabelPositions : undefined
                                            }
                                            onYAxisTickLabelPositions={index === 0 ? setYAxisTickLabelPositions : undefined}
                                            loading={false}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* <div className="mt-3 flex items-center justify-between text-xs text-toolbarLabelGrey">
                <span>{yAxisTitle}</span>
                <span>{xAxisTitle}</span>
            </div> */}
                </>
            )}
        </div>
    );
};

export default PackArchitectureChartWithControls;
